import React from 'react';
import { Button } from '../ui/button';
import { CheckCircle2 } from 'lucide-react';

/* Slide deck for proposals of type `uploaded_pdf`. Each slide is a single
 * page image from the broker's uploaded PDF, rendered full-bleed. The
 * existing live presentation engine drives synced page index between
 * broker + client; we just have to know how to paint one page given the
 * slide payload entry.
 *
 * Slides payload (set by the backend uploader):
 *   payload.slides = [{ page: 1, url: '/api/public/pdf-proposal-slide/<id>/1' }, ...]
 *   payload.pages  = N
 *   payload.filename = 'original.pdf'
 */

// Each slide carries enough info for the registry's slidesFor() to enumerate
// without needing payload at definition time. We override this from
// `slidesFor(payload)` at runtime since the page count is dynamic.
export const UPLOADED_PDF_SLIDES = [
  { id: 'pdf-page-1', key: 'pdf-page-1', label: 'Page 1', title: 'Page 1' },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const PageSlide = ({ pageIndex, slide, payload, accepted, onAccept, readonly }) => {
  const url = slide?.url ? (slide.url.startsWith('http') ? slide.url : `${BACKEND}${slide.url}`) : null;
  const isLast = pageIndex === (payload?.pages || 1) - 1;

  if (!url) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400">
        Slide unavailable
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-full flex flex-col bg-slate-900" data-testid={`pdf-slide-${pageIndex}`}>
      {/* PDF page — fills the viewport while preserving aspect ratio.
          On mobile the page renders full-width; on desktop we cap at max-w-5xl
          and let the height grow until the footer needs space. */}
      <div className="flex-1 flex items-center justify-center overflow-auto px-2 py-3 sm:px-4 sm:py-4">
        <img
          src={url}
          alt={`Proposal page ${slide.page}`}
          className="w-full max-w-5xl h-auto object-contain shadow-2xl rounded-md bg-white"
          style={{ maxHeight: 'calc(100vh - 120px)' }}
          draggable={false}
        />
      </div>

      {/* Footer: page counter + accept button on the last page */}
      <div className="px-4 sm:px-6 py-3 flex items-center justify-between bg-slate-950/80 backdrop-blur">
        <p className="text-xs uppercase tracking-wide text-slate-400">
          Page {slide.page} of {payload?.pages || 1}
          {payload?.filename ? <span className="hidden sm:inline ml-3 text-slate-600">{payload.filename}</span> : null}
        </p>
        {isLast && !readonly && !accepted && onAccept && (
          <Button
            onClick={onAccept}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="pdf-accept-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Accept Proposal
          </Button>
        )}
        {accepted && (
          <span className="text-emerald-400 text-sm font-semibold flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4" /> Accepted
          </span>
        )}
      </div>
    </div>
  );
};

/** Resolve the actual slide list from a proposal payload. */
export const slidesFor = (payload) => {
  const slides = Array.isArray(payload?.slides) ? payload.slides : [];
  if (!slides.length) return UPLOADED_PDF_SLIDES;
  return slides.map((s, idx) => ({
    // Both `id`+`label` (used by some legacy helpers) and `key`+`title`
    // (used by ProposalPresentation's slide-dots / breadcrumbs) — same value.
    id: `pdf-page-${s.page || idx + 1}`,
    key: `pdf-page-${s.page || idx + 1}`,
    label: `Page ${s.page || idx + 1}`,
    title: `Page ${s.page || idx + 1}`,
    _slide: s,
  }));
};

/** Renderer expected by ProposalPresentation / ProposalViewer.
 *  Signature: ({index, payload, clientName, brokerName, readonly, accepted, onAccept}) => JSX
 *  Each PDF page is one slide; the slide list comes from slidesFor(payload). */
export const renderSlide = ({ index, payload, clientName, brokerName, readonly, accepted, onAccept }) => {
  const slides = Array.isArray(payload?.slides) ? payload.slides : [];
  const slide = slides[index] || null;
  return (
    <PageSlide
      pageIndex={index}
      slide={slide}
      payload={payload}
      accepted={accepted}
      readonly={readonly}
      onAccept={onAccept}
    />
  );
};

export default UPLOADED_PDF_SLIDES;
